// Settings → Connections as ONE list, joined on the server (Connections
// "one list", P1): one entry per SERVICE, however many records it has
// underneath (a connection, a data source, a REST API or MCP server from
// before). routes/connections_directory.py; types from the OpenAPI schema
// (schemas/connections_directory.py). Never a secret.
//
//   GET    …/connections/directory               the entries and the agents on offer
//   PUT    …/connections/directory/{id}/access   {agents, since}: ONE scope onto every record
//   DELETE …/connections/directory/{id}          Remove takes every record
//
// The read answers in three ways: the directory; "off" (404: the api runs
// with LATTICE_CONNECTIONS_DIRECTORY=0, or predates the routes), and the
// page then builds its list in the browser as before (connectionRows.
// buildRows); "disabled" (409 connections_disabled), never asked for by a
// page that knows Connections is off. Under the ["connections", c] key, so
// every connections mutation refreshes it too.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch, errorCode } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { connectionsKey } from "./connections";
import { dataSourceKeys } from "./dataSources";

export type DirectoryEntry = components["schemas"]["DirectoryEntry"];
export type DirectoryMember = components["schemas"]["DirectoryMember"];
export type ConnectionDirectory = components["schemas"]["ConnectionDirectory"];
export type DirectoryAccessOut = components["schemas"]["DirectoryAccessOut"];
export type DirectoryAccessKept = components["schemas"]["DirectoryAccessKept"];
export type DirectoryAccessChange = components["schemas"]["DirectoryAccessChange"];
export type DirectoryRemovedOut = components["schemas"]["DirectoryRemovedOut"];

/** What the read found: the list, the switch off on this server (build it in the browser), or Connections off here. */
export type DirectoryRead =
  | { state: "on"; directory: ConnectionDirectory }
  | { state: "off" }
  | { state: "disabled" };

export const directoryKey = (customerId: string | null) => [...connectionsKey(customerId), "directory"];

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/connections/directory`;

/**
 * The one read of the directory, shared by every hook on its key (the
 * pickers' `useConnectionsDirectory` selects from the same answer), so the
 * cache under `directoryKey` always holds a DirectoryRead.
 */
export async function fetchDirectory(customerId: string, signal: AbortSignal | undefined): Promise<DirectoryRead> {
  try {
    const d = await apiFetch<ConnectionDirectory>(base(customerId), { signal });
    return { state: "on", directory: { ...d, entries: d.entries ?? [], agents: d.agents ?? [] } };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return { state: "off" };
    if (e instanceof ApiError && e.status === 409 && errorCode(e) === "connections_disabled") return { state: "disabled" };
    throw e;
  }
}

/**
 * The one list. `enabled`: only once the page knows Connections is on (a
 * workspace with it off never asks). Fresh while shown: an agent can
 * connect something mid-conversation, a data source discovers in the
 * background.
 */
export function useConnectionDirectory(customerId: string | null, enabled = true) {
  return useQuery({
    queryKey: directoryKey(customerId),
    enabled: enabled && !!customerId,
    queryFn: ({ signal }) => fetchDirectory(customerId!, signal),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.state !== "on") return false;
      return d.directory.entries?.some((e) => e.status === "discovering") ? 3_000 : 15_000;
    },
    staleTime: 5_000,
  });
}

/** Every list a member's own route shows: the old reads stay right beside the one list. */
function refreshAll(qc: QueryClient, customerId: string) {
  void qc.invalidateQueries({ queryKey: connectionsKey(customerId) });
  void qc.invalidateQueries({ queryKey: dataSourceKeys.all(customerId) });
  void qc.invalidateQueries({ queryKey: ["integrations", customerId] });
}

/** Put one entry in the cached list as the server answered it (its new version first, so the next save sends it). */
function putEntry(qc: QueryClient, customerId: string, entry: DirectoryEntry) {
  qc.setQueryData<DirectoryRead>(directoryKey(customerId), (before) => {
    if (before?.state !== "on") return before;
    const entries = (before.directory.entries ?? []).map((e) => (e.id === entry.id ? entry : e));
    return { state: "on", directory: { ...before.directory, entries } };
  });
}

/** Who can use it, as ONE scope ("*" and named subagents, or named agents), from the version the page read. */
export function useSetEntryAccess() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, id, agents, since }: { customerId: string; id: string; agents: string[]; since: string }) =>
      apiFetch<DirectoryAccessOut>(`${base(customerId)}/${encodeURIComponent(id)}/access`, { method: "PUT", body: { agents, since } }),
    onSuccess: (out, v) => {
      putEntry(qc, v.customerId, out.entry);
      refreshAll(qc, v.customerId);
    },
  });
}

/** Remove every record of the service; 409 in_use (naming the workflows) removes nothing. */
export function useRemoveEntry() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, id }: { customerId: string; id: string }) =>
      apiFetch<DirectoryRemovedOut>(`${base(customerId)}/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSettled: (_out, _e, v) => refreshAll(qc, v.customerId),
  });
}
